/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import grayscale from './images/layers/grayscale.png';
import satellite from './images/layers/satellite.png';

export const {
  APP_CLIENT,
  APP_NAME,
  MAP_TERMS_OF_USE_URL: APP_TERMS_OF_USE,
  MAP_GRAYSCALE_STYLE,
  MAP_SATELLITE_STYLE,
  MAP_SHOW_DISCLAIMER,
  POWERED_BY_PC_URL,
} = require('@app/config');

const APP_LOGO_MODULE = require(`./images/${APP_CLIENT}_logo.svg`);
export const APP_LOGO =
  typeof APP_LOGO_MODULE === 'object'
    ? APP_LOGO_MODULE.default || APP_LOGO_MODULE
    : APP_LOGO_MODULE;

// Add workspace-specific logos with multiple format support
export const getWorkspaceLogo = (workspace: string | null) => {
  //console.log('getWorkspaceLogo called with workspace:', workspace);
  //console.log('APP_CLIENT:', APP_CLIENT);

  if (!workspace) {
    console.log('No workspace provided, returning default APP_LOGO');
    // Ensure APP_LOGO is also properly handled
    return typeof APP_LOGO === 'object' ? APP_LOGO.default || APP_LOGO : APP_LOGO;
  }

  // Try different formats in order of preference
  const formats = ['svg', 'png']; // , 'jpg', 'jpeg'];

  for (const format of formats) {
    const logoPath = `./images/${APP_CLIENT}_${workspace}_logo.${format}`;
    //console.log('Attempting to load logo from path:', logoPath);

    try {
      const logoModule = require(`./images/${APP_CLIENT}_${workspace}_logo.${format}`);
      //console.log('Successfully loaded logo module from:', logoPath);
      //console.log('Logo module type:', typeof logoModule);
      //console.log('Logo module:', logoModule);

      // Handle different module formats
      let logoUrl;
      if (typeof logoModule === 'string') {
        logoUrl = logoModule;
      } else if (logoModule && typeof logoModule === 'object') {
        // Try different properties that might contain the URL
        logoUrl = logoModule.default || logoModule.src || logoModule.url || logoModule;
      } else {
        logoUrl = logoModule;
      }

      //console.log('Resolved logo URL:', logoUrl);
      //console.log('Logo URL type:', typeof logoUrl);

      // Ensure we return a valid string
      if (typeof logoUrl === 'string' && logoUrl.length > 0 && !logoUrl.includes('[object')) {
        return logoUrl;
      } else {
        console.warn('Invalid logo URL format:', logoUrl);
        continue; // Try next format
      }
    } catch (error) {
      // Continue to next format
      console.warn(
        'Failed to load logo from:',
        logoPath,
        '... trying next format',
        'Error:',
        error?.message || error
      );
    }
  }

  // Fall back to default logo if no workspace-specific logo exists
  //console.log('All logo paths failed, falling back to default APP_LOGO');
  const defaultLogo = typeof APP_LOGO === 'object' ? APP_LOGO.default || APP_LOGO : APP_LOGO;
  console.log('Default logo:', defaultLogo);
  return defaultLogo;
};

export const PAGE_SIZE = 30;

export const APP_BASEMAPS = [
  {
    slug: 'grayscale',
    name: 'Grayscale',
    background: grayscale,
    id: MAP_GRAYSCALE_STYLE,
  },
  {
    slug: 'satellite',
    name: 'Satellite',
    background: satellite,
    id: MAP_SATELLITE_STYLE,
  },
];

export const APP_ABOUT = 'https://github.com/natgeosociety/marapp-frontend/blob/master/ABOUT.md';

export const POWERED_BY_URL = 'https://www.impactobservatory.com/';

// export const MAP_CUSTOM_ATTRIBUTION =
//   APP_CLIENT === 'unbl'
//     ? '<a href="https://www.un.org/geospatial/" target="_blank">UN Geospatial</a>'
//     : null;
